import { useAuth } from "@features/auth/context/AuthContext";
import PaginationNumeric from "@shared/components/PaginationNumeric";
import { Button } from "@shared/components/ui/Button";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useInvitationApprovals } from "../hooks/useInvitationApprovals";
import {
  InvitationApprovalStatus,
  InvitationForReview,
} from "../types/invitation";

const STATUS_LABELS: Record<InvitationApprovalStatus, string> = {
  [InvitationApprovalStatus.PENDING]: "Pending",
  [InvitationApprovalStatus.APPROVED]: "Approved",
  [InvitationApprovalStatus.REJECTED]: "Rejected",
};

const STATUS_CLASSES: Record<InvitationApprovalStatus, string> = {
  [InvitationApprovalStatus.PENDING]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  [InvitationApprovalStatus.APPROVED]:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  [InvitationApprovalStatus.REJECTED]:
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const FILTERS: Array<{ value: InvitationApprovalStatus | "all"; label: string }> = [
  { value: InvitationApprovalStatus.PENDING, label: "Pending" },
  { value: InvitationApprovalStatus.APPROVED, label: "Approved" },
  { value: InvitationApprovalStatus.REJECTED, label: "Rejected" },
  { value: "all", label: "All" },
];

const toDate = (value: any): Date | null => {
  if (!value) return null;
  return value?.toDate ? value.toDate() : new Date(value);
};

/** Compact form for the table, where the columns are tight. */
const formatDate = (value: any): string => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * With the time, for the review dialog: when two admins share a queue, knowing
 * a decision landed minutes ago rather than that morning is the difference
 * between a stale row and someone working on it right now.
 */
const formatDateTime = (value: any): string => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function MemberInvitationsManagement() {
  const { user, isLoading: authLoading } = useAuth();
  const {
    invitations,
    statusFilter,
    changeFilter,
    page,
    setPage,
    total,
    hasNextPage,
    pageSize,
    loading,
    isReviewing,
    error,
    reviewInvitation,
    fetchInvitations,
  } = useInvitationApprovals({ enabled: !authLoading && user?.role === "admin" });

  // Opening a notification while already on this screen navigates to the same
  // route, which React Router serves without remounting anything: the list would
  // sit there stale, showing none of the request that was just announced.
  const location = useLocation();
  const firstVisit = useRef(true);

  useEffect(() => {
    if (firstVisit.current) {
      firstVisit.current = false;
      return;
    }
    fetchInvitations();
  }, [location.key, fetchInvitations]);

  const [selected, setSelected] = useState<InvitationForReview | null>(null);
  const [comment, setComment] = useState("");

  // Same guard the other admin screens use: the route is reachable, the data is not.
  if (!authLoading && (!user || user.role !== "admin")) {
    return <Navigate to="/settings/account" replace />;
  }

  const openReview = (invitation: InvitationForReview) => {
    setSelected(invitation);
    setComment(invitation.reviewComment || "");
  };

  const closeReview = () => {
    setSelected(null);
    setComment("");
  };

  const decide = async (
    status:
      | InvitationApprovalStatus.APPROVED
      | InvitationApprovalStatus.REJECTED,
  ) => {
    if (!selected) return;
    const done = await reviewInvitation(selected.id, {
      status,
      comment: comment.trim() || undefined,
    });
    if (done) closeReview();
  };

  const isPending = (invitation: InvitationForReview) =>
    invitation.approvalStatus === InvitationApprovalStatus.PENDING;

  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = (page - 1) * pageSize + invitations.length;

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: "Member Invitations",
        search: { show: false },
      }}
      isLoading={loading && invitations.length === 0}
      error={error ? new Error(error) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-4 w-full max-w-9xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Organizations request these invitations for new individual members.
          Nothing is emailed to the person until you approve it here.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => changeFilter(filter.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === filter.value
                  ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 whitespace-nowrap">Requested</th>
                  <th className="px-4 py-3">Invited email</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Requested by</th>
                  <th className="px-4 py-3 whitespace-nowrap">Reviewed by</th>
                  <th className="px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invitations.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                    >
                      {statusFilter === InvitationApprovalStatus.PENDING
                        ? "No invitations are waiting for review."
                        : "No invitations found for this filter."}
                    </td>
                  </tr>
                )}
                {invitations.map((invitation) => (
                  <tr
                    key={invitation.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      {formatDate(invitation.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium">
                      {invitation.email}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {invitation.organizationName}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      <span className="block">{invitation.invitedByName}</span>
                      {invitation.invitedByEmail && (
                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                          {invitation.invitedByEmail}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      {invitation.reviewedByName ? (
                        <>
                          <span className="block">
                            {invitation.reviewedByName}
                          </span>
                          <span className="block text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(invitation.reviewedAt)}
                          </span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_CLASSES[invitation.approvalStatus] ||
                          STATUS_CLASSES[InvitationApprovalStatus.PENDING]
                        }`}
                      >
                        {STATUS_LABELS[invitation.approvalStatus] || "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant={isPending(invitation) ? "primary" : "secondary"}
                        onClick={() => openReview(invitation)}
                      >
                        {isPending(invitation) ? "Review" : "View"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
              <PaginationNumeric
                currentPage={page}
                totalPages={Math.max(1, Math.ceil(total / pageSize))}
                onPageChange={setPage}
              />
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
                Showing {firstRow}–{lastRow} of {total}
              </p>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <>
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 transition-opacity"
            aria-hidden="true"
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-3 ${
                        STATUS_CLASSES[selected.approvalStatus] ||
                        STATUS_CLASSES[InvitationApprovalStatus.PENDING]
                      }`}
                    >
                      {STATUS_LABELS[selected.approvalStatus] || "Pending"}
                    </span>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {selected.email}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      to join {selected.organizationName}
                    </p>
                  </div>
                  <button
                    onClick={closeReview}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label="Close"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                      Requested by
                    </dt>
                    <dd className="text-gray-800 dark:text-gray-200">
                      {selected.invitedByName}
                      {selected.invitedByEmail && (
                        <span className="block text-gray-500 dark:text-gray-400">
                          {selected.invitedByEmail}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                      Requested on
                    </dt>
                    <dd className="text-gray-800 dark:text-gray-200">
                      {formatDateTime(selected.createdAt)}
                    </dd>
                  </div>
                </dl>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  {isPending(selected) ? (
                    <>
                      <label
                        htmlFor="review-comment"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Internal note (optional)
                      </label>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                        Only GCCA admins see this. The rejection email tells the
                        organization to contact a GCCA admin, and never includes
                        this note.
                      </p>
                      <textarea
                        id="review-comment"
                        className="form-textarea w-full mb-5"
                        rows={3}
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder="Why this was approved or rejected"
                      />
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="secondary" onClick={closeReview}>
                          Cancel
                        </Button>
                        <Button
                          variant="error"
                          disabled={isReviewing}
                          isLoading={isReviewing}
                          onClick={() =>
                            decide(InvitationApprovalStatus.REJECTED)
                          }
                        >
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          disabled={isReviewing}
                          isLoading={isReviewing}
                          onClick={() =>
                            decide(InvitationApprovalStatus.APPROVED)
                          }
                        >
                          Approve &amp; send
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        This invitation was already reviewed by{" "}
                        {selected.reviewedByName || "an admin"} on{" "}
                        {formatDateTime(selected.reviewedAt)}.
                      </p>
                      {selected.reviewComment && (
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                            Internal note
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            {selected.reviewComment}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button variant="secondary" onClick={closeReview}>
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AuthenticatedLayout>
  );
}
